import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Card, Grid, Theme, Typography } from '@mui/material';
import { createStyles, withStyles, WithStyles } from '@mui/styles';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { AppModules } from 'src/services/api/models';
import RoleBaseAccess from 'src/services/RoleBaseAccess';
import Store from 'src/stores/RootStore';
import { TCartItem } from 'src/stores/types/CartItemModel';
import { ProductCategoryTypes } from 'src/utils/constants';
import CartItem from './CartItem';
import CartDetailsInclusive from './CartDetailsInclusive';

export interface CartInclusiveProps extends WithStyles<typeof styles> {
    path: string;
    store?: typeof Store.Type;
}

// Cart for Brekr
const CartInclusive = inject('store')(
    observer(({ classes, store }: CartInclusiveProps) => {
        // Show item details modal
        const displayItem = (cartItem: TCartItem) => {
            if (cartItem.product!.type === ProductCategoryTypes.BIKE) {
                store!.products.setSelectedBikeHpCode(cartItem.id);
                if (cartItem && cartItem.battery && cartItem.battery.code) {
                    store!.products.setSelectedBikeBatteryCode(cartItem.battery.code);
                }
                store!.products.toggleProductDetailDialog(true);
            } else if (cartItem.product!.type === ProductCategoryTypes.MARKETING) {
                store!.marketingMaterials.setSelectedItemId(cartItem.id);
                store!.marketingMaterials.toggleMarketingMaterialDetailsDialog(true);
            } else {
                store!.spareParts.setSelectedSparePartId(cartItem.id);
                store!.spareParts.toggleSparePartDetailDialog(true);
            }
        };

        const renderCartItems = () =>
            store?.cart.listItems.map((item: TCartItem, index: number) => {
                return (
                    <CartItem
                        key={`${item.id}${
                            item.product.type === ProductCategoryTypes.BIKE && item.battery?.code
                                ? '-' + item.battery.code
                                : ''
                        }-${index}`}
                        item={item}
                        removeCartItem={() => store!.cart.removeCartItem(item)}
                        viewItem={(viewItem: TCartItem) => displayItem(viewItem)}
                    />
                );
            });

        const renderCartContent = () => {
            let cartItems = renderCartItems();

            const emptyCartMessage = (
                <Card className={classes.emptyCart} data-testid="empty-cart">
                    <Typography>
                        <FormattedMessage id="cart.empty_cart" />
                    </Typography>
                </Card>
            );
            return (
                <Grid container spacing={2} className={classes.cartHolder}>
                    <Grid item sm={12} className={classes.cartItems}>
                        {RoleBaseAccess.isModuleAllowed(AppModules.ORDER_BIKES) ? cartItems : emptyCartMessage}
                    </Grid>

                    <Grid item sm={12} className={classes.cartDetails}>
                        <CartDetailsInclusive />
                    </Grid>
                </Grid>
            );
        };

        return (
            <>
                <Grid container spacing={3} className={classes.cart}>
                    <Grid item sm={10} xs={9}>
                        <Typography variant="h5" className={classes.cartTitle}>
                            <FormattedMessage id="cart.title" />
                            <span
                                className={`
                                        ${classes.badge}
                                        ${classes.activeBadge}
                                    `}
                            >
                                {store!.cart.totalItemsQuantity}
                            </span>
                        </Typography>
                    </Grid>
                    <Grid item sm={2} xs={3}>
                        {store!.config ? (
                            <Button onClick={store!.config.toggleCart} data-testid="cart-close">
                                <FontAwesomeIcon icon={faTimes} size="lg" />
                            </Button>
                        ) : null}
                    </Grid>
                </Grid>
                {renderCartContent()}
            </>
        );
    }),
);

function styles({ breakpoints, spacing, palette }: Theme) {
    return createStyles({
        cart: {
            boxShadow: 'none',
            padding: '20px 20px 10px 20px',
        },
        cartTitle: {
            marginBottom: spacing(2),
        },
        cartHolder: {
            padding: '10px 20px',
            alignItems: 'stretch',
            alignContent: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            flexWrap: 'nowrap',
            justifyContent: 'flex-start',
            height: 'calc(100vh - 95px)',
        },
        cartItems: {
            overflowY: 'scroll',
        },
        cartDetails: {
            flex: 'none',
        },
        emptyCart: {
            flexGrow: 1,
            maxWidth: '100%',
            margin: '0 0 1.5%',
            boxShadow: 'none',
            justifyContent: 'space-between',
            [breakpoints.down('sm')]: {
                maxWidth: '100%',
                margin: '0 0 10%',
            },
        },
        badge: {
            backgroundColor: '#eee',
            color: palette.text.primary,
            borderRadius: '50%',
            fontSize: '0.8rem',
            width: '1.5rem',
            height: '1.5rem',
            display: 'inline-block',
            position: 'relative',
            left: '3px',
            top: '-8px',
            textAlign: 'center',
            lineHeight: '1.5rem',
        },
        activeBadge: {
            background: palette.secondary.main,
            color: 'white',
        },
    });
}

export default withStyles(styles)(CartInclusive);
